import React, { useState, useEffect, useCallback } from 'react'; // <-- Imported useCallback
import { useNavigate } from 'react-router-dom';
import logo from './wysupp-icon-green-transparent.svg';
import AccountInfo from './AccountInfo';
import { FaDesktop, FaMobileAlt, FaGlobe, FaSignOutAlt, FaArrowLeft } from 'react-icons/fa';
import axiosInstance from './api/axios'; // <-- Import our configured axiosInstance

// Helper function (remains the same)
const getDeviceIcon = (deviceInfo) => {
  if (!deviceInfo) return <FaGlobe />;
  const lowerCaseInfo = deviceInfo.toLowerCase();
  if (lowerCaseInfo.includes('windows') || lowerCaseInfo.includes('mac') || lowerCaseInfo.includes('linux')) return <FaDesktop />;
  if (lowerCaseInfo.includes('iphone') || lowerCaseInfo.includes('android') || lowerCaseInfo.includes('mobile')) return <FaMobileAlt />;
  return <FaGlobe />;
};

export default function ConnectedDevicesList() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // --- FIX: Removed local username state, as it's not needed here ---
  
  const navigate = useNavigate();

  // --- FIX: Removed redundant useEffect that manually verified the token ---
  // Our <ProtectedRoute> component already handles this for us.

  // --- FIX: All API calls now use axiosInstance for automatic auth ---
  const fetchDevices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get('/api/user/sessions');
      setDevices(response.data);
      setLoading(false); // Stop loading on success
    } catch (error) {
      // --- TIMEOUT FIX: Prevent error flash on timeout ---
      if (error.response?.status === 401) return;
      
      setError(error.response?.data?.detail || "Failed to fetch sessions.");
      setLoading(false); // Stop loading on actual errors
    }
  }, []);

  // Fetch data on component mount
  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]); // Dependency array is now correct

  // Function to revoke a device session
  const handleRevokeDevice = async (deviceId) => {
    if (!window.confirm("Are you sure you want to disconnect this device session?")) {
      return;
    }
    try {
      await axiosInstance.delete(`/api/user/sessions/${deviceId}`);
      fetchDevices();
    } catch (err) {
      // --- TIMEOUT FIX: Prevent Zombie Alert on timeout ---
      if (err.response?.status === 401) return;
      
      alert(`Error: ${err.response?.data?.detail || 'Failed to disconnect device.'}`);
    }
  };

  // The rest of your JSX remains the same
  return (
    <div className="devices-page-container">
      <header className="dashboard-header">
        <div className="logo-section">
          <img src={logo} className="logo-icon" alt="Wysupp Logo"/>
          <span className="logo-text">Wysupp Validate</span>
          <span className="logo-subtext">Data Quality Management</span>
        </div>
        <AccountInfo />
      </header>

      <main className="devices-main-content">
        <div className="page-header">
          <button onClick={() => navigate('/home')} className="back-button">
            <FaArrowLeft /> Back
          </button>
          <h1 className="page-title">Connected Devices</h1>
        </div>

        {loading && (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading sessions...</p>
          </div>
        )}

        {error && <p className="error-state">Error: {error}</p>}

        {!loading && !error && (
          <div className="devices-list">
            {devices.length === 0 ? (
              <p className="info-state">No device sessions found.</p>
            ) : (
              devices.map((device) => (
                <div key={device.id} className={`device-card ${device.is_current ? 'current-device' : ''}`}>
                  <div className="device-card-icon">
                    {getDeviceIcon(device.device_info)}
                  </div>
                  <div className="device-card-info">
                    <h3 className="device-name">{device.device_info || 'Unknown Device'}</h3>
                    <p className="device-details">
                      Last active: {new Date(device.last_active_at).toLocaleString()}
                    </p>
                    <p className="device-details">
                      IP Address: {device.ip_address || 'N/A'}
                    </p>
                  </div>
                  <div className="device-card-actions">
                    {device.is_current ? (
                      <span className="current-session-badge">Current Session</span>
                    ) : (
                      <button onClick={() => handleRevokeDevice(device.id)} className="disconnect-button">
                        <FaSignOutAlt /> Disconnect
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </main>
      <style>{`
                body {
                    background-color: var(--dark-bg);
                }
                :root {
                    --dark-bg: #1a202c;
                    --card-bg: #2d3748;
                    --text-color: #e2e8f0;
                    --light-text: #a0aec0;
                    --green-accent: #48bb78;
                    --border-color: #4a5568;
                    --error-color: #ef4444;
                    --error-bg: rgba(239, 68, 68, 0.1);
                }

                .devices-page-container {
                    background-color: var(--dark-bg);
                    min-height: 100vh;
                    color: var(--text-color);
                    font-family: 'Inter', sans-serif;
                }

                /* Header copied from Home.js for consistency */
                .dashboard-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 1rem 3rem;
                    background-color: var(--card-bg);
                    border-bottom: 1px solid var(--border-color);
                    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                }
                .logo-section { display: flex; align-items: center; gap: 0.5rem; }
                .logo-icon { width: 30px; height: 30px; }
                .logo-text { font-size: 1.25rem; font-weight: 700; color: var(--green-accent); }

                .devices-main-content {
                    max-width: 900px;
                    margin: 2rem auto;
                    padding: 0 2rem;
                }

                .page-header {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    margin-bottom: 2rem;
                }

                .back-button {
                    background: var(--card-bg);
                    color: var(--text-color);
                    border: 1px solid var(--border-color);
                    padding: 0.5rem 1rem;
                    border-radius: 6px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: background-color 0.2s;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }
                .back-button:hover {
                    background: #4a5568;
                }

                .page-title {
                    font-size: 2rem;
                    font-weight: 700;
                    color: var(--text-color);
                }

                .loading-state, .error-state, .info-state {
                    text-align: center;
                    padding: 3rem;
                    background-color: var(--card-bg);
                    border-radius: 8px;
                    color: var(--light-text);
                }
                .error-state { color: var(--error-color); background-color: var(--error-bg); }
                .loading-spinner {
                    width: 40px;
                    height: 40px;
                    margin: 0 auto 1rem auto;
                    border: 4px solid var(--border-color);
                    border-top-color: var(--green-accent);
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                }
                @keyframes spin { to { transform: rotate(360deg); } }

                .devices-list {
                    display: flex;
                    flex-direction: column;
                    gap: 1rem;
                }

                .device-card {
                    background-color: var(--card-bg);
                    border-radius: 8px;
                    padding: 1.5rem;
                    display: flex;
                    align-items: center;
                    gap: 1.5rem;
                    border-left: 4px solid var(--border-color);
                    transition: box-shadow 0.2s;
                }
                .device-card:hover {
                    box-shadow: 0 4px 15px rgba(0,0,0,0.2);
                }
                .device-card.current-device {
                    border-left-color: var(--green-accent);
                }

                .device-card-icon {
                    font-size: 2.5rem;
                    color: var(--light-text);
                }

                .device-card-info {
                    flex-grow: 1;
                }
                
                .device-name {
                    font-size: 1.1rem;
                    font-weight: 600;
                    color: var(--text-color);
                    margin: 0 0 0.25rem 0;
                }

                .device-details {
                    font-size: 0.85rem;
                    color: var(--light-text);
                    margin: 0;
                }
                
                .device-card-actions {
                    flex-shrink: 0;
                }

                .current-session-badge {
                    background-color: rgba(72, 187, 120, 0.1);
                    color: var(--green-accent);
                    padding: 0.4rem 0.8rem;
                    border-radius: 99px;
                    font-size: 0.8rem;
                    font-weight: 600;
                }
                
                .disconnect-button {
                    background: transparent;
                    color: var(--error-color);
                    border: 1px solid var(--error-color);
                    padding: 0.5rem 1rem;
                    border-radius: 6px;
                    font-weight: 600;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    transition: background-color 0.2s, color 0.2s;
                }
                .disconnect-button:hover {
                    background-color: var(--error-color);
                    color: var(--dark-bg);
                }
                .logo-subtext {
                    font-size: 0.75rem;
                    color: var(--light-text);
                    margin-left: 0.5rem;
                    padding-left: 0.5rem;
                    border-left: 1px solid var(--light-text);
                }
            `}</style>
    </div>
  );
}